import {
	LookTable,
	LookTableEncoding,
	LookTableRGBGamma,
	LookTableRGBPrimaries,
	LookTableType,
	LookTableVersion,
} from "@/core/types"
import { decodeTableFromString } from "@/core/dcp/string-table"
import { vec3 } from "gl-matrix"
import { uuid } from "@/utils/uuid"

export const tableMaxTotalSamples = 36 * 32 * 16
export const tableMaxHueSamples = 360
export const tableMaxSatSamples = 256
export const tableMaxValSamples = 25

const tableRGBMinDivisions1D = 2
const tableRGBMaxDivisions1D = 4096
const tableRGBMinDivisions3D = 2
const tableRGBMaxDivisions3D = 32

enum TableRGBGamut {
	clip = 0,
	extend,
	invalid,
}

export enum TableType {
	lookTable = 0,
	rgbTable = 1,
}

/**
 * Each entry of the table contains three 32-bit IEEE floating-point values. The first entry is hue shift in degrees,
 * the second entry is a saturation scale factor, and the third entry is a value scale factor.
 *
 * The table entries are stored in the tag in nested loop order, with the value divisions in the outer loop, the hue
 * divisions in the middle loop, and the saturation divisions in the inner loop.
 *
 * (x = sat, y = hue, z = val) so if data is laid out in a 2d texture, then
 * width = satDivisions, height = hueDivisions * valDivisions
 */
export function decodeLookTable(lookTable: string): LookTable {
	const result = decodeTableFromString(lookTable)
	const dataView = new DataView(result.buffer, result.byteOffset, result.byteLength)
	const type = dataView.getUint32(0, true)

	if (type === TableType.lookTable) {
		return decodeLookHSVTable(result)
	} else {
		return decodeLookRGBTable(result)
	}
}

function wrapValue(value: number, max: number) {
	const wrappedValue = value % max

	// prevent wrap to 0 when value == max * n
	if (value > 0 && wrappedValue === 0) {
		return max
	}
	return wrappedValue
}

function decodeLookRGBTable(bytes: Uint8Array): LookTable {
	let offset = 0
	const stream = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)

	function getUint32() {
		const value = stream.getUint32(offset, true)
		offset += 4
		return value
	}

	function getUint16() {
		const value = stream.getUint16(offset, true)
		offset += 2
		return value
	}

	function getFloat64() {
		const value = stream.getFloat64(offset, true)
		offset += 8
		return value
	}

	if (getUint32() != TableType.rgbTable) {
		throw new Error("Not a RGB table")
	}

	if (getUint32() !== LookTableVersion.version1) {
		throw new Error("Unknown RGB table version")
	}

	const fDimensions = getUint32()
	const fDivisions = getUint32()

	if (
		fDimensions === 1 &&
		(fDivisions < tableRGBMinDivisions1D || fDivisions > tableRGBMaxDivisions1D)
	) {
		throw new Error("Invalid 1D divisions")
	} else if (
		fDimensions === 3 &&
		(fDivisions < tableRGBMinDivisions3D || fDivisions > tableRGBMaxDivisions3D)
	) {
		throw new Error("Invalid 3D divisions")
	} else if (fDimensions !== 1 && fDimensions !== 3) {
		throw new Error("Invalid dimensions")
	}

	const nopValue = new Uint16Array(fDivisions)

	for (let i = 0; i < fDivisions; i++) {
		nopValue[i] = (i * 0x0ffff + (fDivisions >> 1)) / (fDivisions - 1)
	}

	let samples
	let index = 0

	if (fDimensions === 1) {
		samples = new Uint16Array(fDivisions * 3)

		for (let i = 0; i < fDivisions; i++) {
			// Wrap around for 65535 is necessary to follow the DNG spec behaviour.

			const rVal = wrapValue((getUint16() ?? 0) + (nopValue[i] ?? 0), 65535)
			const gVal = wrapValue((getUint16() ?? 0) + (nopValue[i] ?? 0), 65535)
			const bVal = wrapValue((getUint16() ?? 0) + (nopValue[i] ?? 0), 65535)

			samples[index++] = rVal / 65535.0
			samples[index++] = gVal / 65535.0
			samples[index++] = bVal / 65535.0
		}
	} else {
		samples = new Float32Array(fDivisions * fDivisions * fDivisions * 3)

		for (let r = 0; r < fDivisions; r++) {
			for (let g = 0; g < fDivisions; g++) {
				for (let b = 0; b < fDivisions; b++) {
					// Wrap around for 65535 is necessary to follow the DNG spec behaviour.

					const rVal = wrapValue((getUint16() ?? 0) + (nopValue[r] ?? 0), 65535)
					const gVal = wrapValue((getUint16() ?? 0) + (nopValue[g] ?? 0), 65535)
					const bVal = wrapValue((getUint16() ?? 0) + (nopValue[b] ?? 0), 65535)

					samples[index++] = rVal / 65535.0
					samples[index++] = gVal / 65535.0
					samples[index++] = bVal / 65535.0
				}
			}
		}
	}

	const fPrimaries = getUint32()

	if (fPrimaries >= LookTableRGBPrimaries.invalid) {
		throw new Error("Unknown RGB table primaries")
	}

	const fGamma = getUint32()
	if (fGamma >= LookTableRGBGamma.invalid) {
		throw new Error("Unknown RGB table gamma")
	}

	const fGamut = getUint32()
	if (fGamut >= TableRGBGamut.invalid) {
		throw new Error("Unknown RGB table gamut processing option")
	}

	const fMinAmount = getFloat64()
	const fMaxAmount = getFloat64()

	if (fMinAmount < 0.0 || fMinAmount > 1.0 || fMaxAmount < 1.0) {
		throw new Error("Invalid min/max amount for RGB table")
	}

	return {
		id: uuid(),
		type: LookTableType.rgb,
		version: LookTableVersion.version1,
		encoding:
			fGamma === LookTableRGBGamma.sRGB ? LookTableEncoding.sRGB : LookTableEncoding.linear,
		hueDivisions: fDivisions,
		satDivisions: fDivisions,
		valDivisions: fDivisions,
		minAmount: fMinAmount,
		maxAmount: fMaxAmount,
		data: samples,
		rgbPrimaries: fPrimaries,
		rgbGamma: fGamma,
	}
}

function decodeLookHSVTable(result: Uint8Array): LookTable {
	const dataView = new DataView(result.buffer, result.byteOffset, result.byteLength)

	const version = dataView.getUint32(4, true)

	if (version !== LookTableVersion.version1 && version !== LookTableVersion.version2) {
		throw new Error("Unknown look table version")
	}

	const hueDivisions = dataView.getUint32(8, true)
	const satDivisions = dataView.getUint32(12, true)
	const valDivisions = dataView.getUint32(16, true)

	const totalSamples = hueDivisions * satDivisions * valDivisions
	const size = totalSamples * 3

	if (
		hueDivisions < 1 ||
		satDivisions < 1 ||
		valDivisions < 1 ||
		hueDivisions > tableMaxHueSamples ||
		satDivisions > tableMaxSatSamples ||
		valDivisions > tableMaxValSamples ||
		totalSamples > tableMaxTotalSamples
	) {
		throw new Error("Invalid table dimensions")
	}

	const dataStart = 5
	const dataEnd = dataStart + size
	const data = new Float32Array(size)
	for (let i = 0; i < size; i++) {
		data[i] = dataView.getFloat32((dataStart + i) * 4, true)
	}

	const encoding = dataView.getUint32(dataEnd * 4, true)

	if (encoding !== LookTableEncoding.linear && encoding !== LookTableEncoding.sRGB) {
		throw new Error("Unknown look table encoding")
	}

	let minAmount = 1
	let maxAmount = 1

	if (version !== 1) {
		const minAmountOffset = dataEnd * 4 + 8
		const maxAmountOffset = dataEnd * 4 + 16

		minAmount = dataView.getFloat64(minAmountOffset, true)
		maxAmount = dataView.getFloat64(maxAmountOffset, true)

		if (minAmount < 0.0 || minAmount > 1.0 || maxAmount < 1.0) {
			throw new Error("Invalid min/max amount for look table")
		}
	}

	// Normalize data
	for (let i = 0; i < data.length; i += 3) {
		data[i] = (data[i] || 0) / 360 // Normalize hue shift
	}

	return {
		id: uuid(),
		type: LookTableType.hsv,
		version,
		encoding,
		hueDivisions,
		satDivisions,
		valDivisions,
		minAmount,
		maxAmount,
		data,
		rgbPrimaries: LookTableRGBPrimaries.invalid,
		rgbGamma: LookTableRGBGamma.invalid,
	}
}

/**
 * Layout of look table data is as follows:
 * Each block is a value division
 * Each row is a hue division
 * Each column in a saturation division
 *  ┌───┬──────────────
 *  │ h │ s, s, s, ...
 *  v h │ s, s, s, ...
 *  │ h │ s, s, s, ...
 *  ├───┼──────────────
 *  │ h │ s, s, s, ...
 *  v h │ s, s, s, ...
 *  │ h │ s, s, s, ...
 *  └───┴──────────────
 */
export function createLookTable(dims: vec3, entries: number[]): LookTable {
	const [hueDivisions, satDivisions, valDivisions] = dims
	const data = new Float32Array(entries)

	return {
		id: uuid(),
		type: LookTableType.hsv,
		version: LookTableVersion.version1,
		encoding: LookTableEncoding.linear,
		minAmount: 1,
		maxAmount: 1,
		hueDivisions,
		satDivisions,
		valDivisions,
		data,
		rgbGamma: LookTableRGBGamma.invalid,
		rgbPrimaries: LookTableRGBPrimaries.invalid,
	}
}
